// §26.1 / §26.10 (D-220) — namespaced identity, and the two functions that make it non-optional.
//
// Every user reference in §26's tables and payloads is `'<userId>@<instance>'`, never a bare id. A
// local user is `'u_abc@hangar.ab8.org'`; a future `someone@hangar.capct.org` is a value these
// columns already hold. **Nothing routes across instances in v1** — this buys the option, not the
// feature, and the cost of buying it now rather than later is one migration that never has to
// happen.
//
// The load-bearing rule is that **a bare id is rejected, never coerced**. It would be one line to
// make `parseRef('u_abc')` return `{ id: 'u_abc', instance: HANGAR_INSTANCE }`, and that line is
// the whole bug: every place that forgot to namespace would keep working, locally, until the day a
// second instance existed — at which point rows written by the tolerant path would silently mean
// "whoever has that id here". A ref is either namespaced or it is not a ref.

/** A parsed user reference. Both halves are non-empty by construction. */
export interface UserRef {
  readonly id: string;
  readonly instance: string;
}

/**
 * A user id: what `users.id` holds. Deliberately narrow — no `@`, so a ref can never be ambiguous
 * about where to split, and no whitespace, so a value that round-trips through a URL or a log line
 * comes back the same.
 */
const ID_PATTERN = /^[A-Za-z0-9_.:-]+$/;

/**
 * An instance: a hostname. Not a URL — no scheme, no path, no port. `HANGAR_INSTANCE` is
 * `hangar.ab8.org`, which is what makes a ref readable at a glance and comparable by `===`.
 */
const INSTANCE_PATTERN = /^[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?)*$/;

export class UserRefError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UserRefError';
  }
}

export const isUserId = (value: string): boolean => ID_PATTERN.test(value);
export const isInstance = (value: string): boolean =>
  value.length > 0 && value.length <= 253 && INSTANCE_PATTERN.test(value);

/**
 * Build a ref. Throws on either half being malformed rather than producing a string that looks
 * like a ref and is not.
 */
export function formatRef(id: string, instance: string): string {
  if (!isUserId(id)) throw new UserRefError(`not a user id: ${JSON.stringify(id)}`);
  if (!isInstance(instance)) throw new UserRefError(`not an instance: ${JSON.stringify(instance)}`);
  return `${id}@${instance}`;
}

/**
 * Split a ref, or `null`.
 *
 * `null` rather than a throw because this is the *reading* half and it runs against values that
 * arrived from a request body — a malformed one is a 400, not a 500. `formatRef` throws because it
 * runs against values this codebase produced, where a malformed one is a bug.
 */
export function parseRef(ref: string): UserRef | null {
  // Exactly one `@`, and nothing empty either side. `indexOf`/`lastIndexOf` rather than `split`, so
  // `a@b@c` is refused instead of silently becoming `a` at `b@c`.
  const at = ref.indexOf('@');
  if (at <= 0 || at !== ref.lastIndexOf('@') || at === ref.length - 1) return null;
  const id = ref.slice(0, at);
  const instance = ref.slice(at + 1);
  if (!isUserId(id) || !isInstance(instance)) return null;
  return { id, instance };
}

/** `true` for a well-formed ref. The predicate `parseRef` is built from, named for readability. */
export const isRef = (value: string): boolean => parseRef(value) !== null;

/** A ref belonging to this instance — the only kind v1 ever acts on. */
export const isLocalRef = (ref: string, instance: string): boolean =>
  parseRef(ref)?.instance === instance;

/**
 * The local id inside a ref, or `null` if the ref is malformed **or belongs elsewhere**.
 *
 * One function rather than "parse then compare" at every call site, because the comparison is the
 * step it is easy to leave out — and leaving it out is how a remote ref reaches a query that
 * assumes `users.id`.
 */
export function localIdOf(ref: string, instance: string): string | null {
  const parsed = parseRef(ref);
  return parsed !== null && parsed.instance === instance ? parsed.id : null;
}

// ── §26.3 connection state ───────────────────────────────────────────────────────────────────

export const CONNECTION_STATES = ['pending_out', 'pending_in', 'accepted', 'blocked'] as const;
export type ConnectionState = (typeof CONNECTION_STATES)[number];

export interface Connection {
  /** The peer, as a ref. The caller is always the other half and is never echoed back. */
  readonly peer: string;
  /** The peer's display name, resolved for a local ref; the raw ref otherwise. */
  readonly displayName: string;
  readonly state: ConnectionState;
  readonly createdAt: number;
  readonly acceptedAt: number | null;
}

// ── §26.2 presence ───────────────────────────────────────────────────────────────────────────

/**
 * §26.2 — the presence states, and which of them a person may *choose*.
 *
 * `busy` joined at D-344. The other three predate it and two of them are not choices at all:
 * `offline` is what the clock says about somebody who has stopped checking in, and `idle` is both
 * a choice (*Away*) and what the clock demotes you to after 90 seconds. Saying which are
 * choosable, here, is what stops a status picker offering "Offline" — a state you cannot enter and
 * would not mean if you could, because the next heartbeat contradicts it.
 */
export const PRESENCE_STATES = ['online', 'idle', 'busy', 'offline'] as const;
export type PresenceState = (typeof PRESENCE_STATES)[number];

/** The three a person picks: *Active*, *Away*, *Busy*. Never `offline`, which is the clock's. */
export const CHOOSABLE_PRESENCE = ['online', 'idle', 'busy'] as const;
export type ChosenPresence = (typeof CHOOSABLE_PRESENCE)[number];

export const isChosenPresence = (value: string): value is ChosenPresence =>
  (CHOOSABLE_PRESENCE as readonly string[]).includes(value);

export interface Presence {
  readonly peer: string;
  readonly status: PresenceState;
  /** An Arcade app id or `null` — never an arbitrary app id (D-215). */
  readonly gameId: string | null;
  readonly updatedAt: number;
}

/**
 * §26.2 — idle at 90 s without a heartbeat, offline at 300 s, **computed on read**.
 *
 * A pure function of two numbers, so it is unit-testable without a clock and without a database,
 * and so there is no sweeper: a sweeper is a background job that can be down, and the one thing
 * worse than stale presence is presence that is stale only sometimes.
 */
export const PRESENCE_IDLE_MS = 90_000;
export const PRESENCE_OFFLINE_MS = 300_000;

export function derivePresence(updatedAt: number, now: number, stored: PresenceState): PresenceState {
  const age = now - updatedAt;
  if (age >= PRESENCE_OFFLINE_MS) return 'offline';
  if (age >= PRESENCE_IDLE_MS) return 'idle';
  // A heartbeat inside the window still says what it says: someone who set themselves `idle` or
  // `busy` deliberately is that, and only the *clock* may demote a status, never the reverse. A
  // stale `busy` becomes `idle` and then `offline` like anything else — the point of the clock is
  // that it describes the connection, and a connection that has stopped talking is not busy.
  return stored;
}

/**
 * §26.2 / D-215 — the app ids presence may report.
 *
 * The twenty Arcade titles and nothing else. A presence row carrying `flightdeck` would tell every
 * connection what work someone is doing, which is a different product from "playing Conquest".
 */
export const ARCADE_GAME_IDS: readonly string[] = [
  'solitaire',
  '2048',
  'cascade',
  'belt',
  'intercept',
  'chomp',
  'conquest',
  'skipper',
  'wayfarer',
  // §29 — Arcade Wave 2 (D-364). Same rule, same reason: an Arcade id or nothing, so a presence
  // row can never say what work somebody is doing.
  'freecell',
  'pyramid',
  'chess',
  'backgammon',
  'phalanx',
  'rivet',
  'burrow',
  'vines',
  'sortie',
  'pulse',
  // §32 — Timber. Missed when it was added, which is exactly the failure this list's own test
  // exists to catch: presence would have refused to say anybody was playing it.
  'timber',
];

export const isArcadeGameId = (value: string): boolean => ARCADE_GAME_IDS.includes(value);

// ── §26.6 the doorbell ───────────────────────────────────────────────────────────────────────

/**
 * §26.6 / §27 — the frame kinds.
 *
 * `reminder` joined at §27 (D-340): a shared list that two people are both editing needs the same
 * "something changed, go and look" that chat does, and a second stream for it would be a second
 * connection per user carrying the same nothing.
 */
export const SOCIAL_EVENTS = [
  'presence',
  'chat',
  'invite',
  'move',
  'reminder',
  // §34.13 (M66) — a file offer arrived, or one you sent was answered. The same doorbell for the
  // fifth time: one stream per user carries every kind of frame (§26.6), and a second stream for
  // offers would be a second connection to hold open for the same person.
  'file_offer',
] as const;
export type SocialEventKind = (typeof SOCIAL_EVENTS)[number];

/**
 * What a frame on `/api/social/events` carries: **that** something changed, never what.
 *
 * D-217's rule, encoded as a type. There is no `body`, no `state` and no `payload` field, and there
 * is deliberately nowhere to put one — the client `GET`s the thing the frame names. A message body
 * riding the stream would be a second delivery path with different reliability semantics from the
 * `GET`, in the same codebase, for the same data.
 */
export interface SocialEvent {
  readonly kind: SocialEventKind;
  /** Who it is about, as a ref. Enough to know which thread or roster row to refetch. */
  readonly from: string;
  /** For `invite` and `move`: which play session. Never the moves themselves. */
  readonly sessionId?: string;
  /**
   * §34.13 — for `file_offer`: which offer. Never the file, its bytes or its sha.
   *
   * Spelled `offerId` rather than §34.13's bare `id` for the same reason `sessionId` is not `id`:
   * on a frame that carries a `kind`, an unqualified `id` cannot say what it identifies, and this
   * type already had one field that had to answer that question (D-581).
   */
  readonly offerId?: string;
}

// ── §26.3 the invite link ────────────────────────────────────────────────────────────────────

/** 256 bits, hex — 64 characters. The token *is* the secret (§26.9), so there is nothing else in it. */
export const INVITE_TOKEN_BYTES = 32;
export const INVITE_TOKEN_PATTERN = /^[0-9a-f]{64}$/;
export const isInviteToken = (value: string): boolean => INVITE_TOKEN_PATTERN.test(value);

/** The link §26.3 offers to copy. Built from the base URL so a test instance never emits a live one. */
export const inviteLink = (baseUrl: string, token: string): string =>
  `${baseUrl.replace(/\/+$/, '')}/#/connect/${token}`;

// ── §26.5 chat ───────────────────────────────────────────────────────────────────────────────

/** §26.5 — 4096 characters, text (D-216). */
export const MAX_MESSAGE_CHARS = 4096;

/** §26.5 — "a message pane paging backwards 50 at a time"; §16 — "≤ 100". */
export const CHAT_PAGE_SIZE = 50;
export const CHAT_PAGE_MAX = 100;

/**
 * One message, as every §26.5 payload carries it.
 *
 * **`body` is `null` exactly when `deleted` is true** (D-218, D-322). The row survives so an open
 * page does not renumber under a reader, and the text does not — "either party deletes, which
 * removes it for both" is not a rendering decision, so there is nothing left here to render.
 *
 * There is no `readAt`. `messages.read_at` backs the unread count and is never surfaced to the
 * sender: §26.5 says no read receipts, and the way to keep that promise is to give the wire no
 * field to put one in (the same instinct as `SocialEvent`'s missing body).
 */
export interface Message {
  readonly id: string;
  /** The sender, as a ref. */
  readonly from: string;
  /** The recipient, as a ref. */
  readonly to: string;
  readonly body: string | null;
  readonly sentAt: number;
  readonly deleted: boolean;
}

/**
 * §26.5 — one thread per **connection**, not per conversation.
 *
 * A roster row with no messages is still a thread: it is where a first message is written. Keying
 * the list on the connection rather than on `SELECT DISTINCT` over `messages` is what stops the
 * Chat app needing a separate "start a conversation" flow — and there is no directory to start one
 * from (§26.3).
 */
export interface Thread {
  readonly peer: string;
  readonly displayName: string;
  /** Messages from the peer that this user has not opened the thread on. Never the peer's count. */
  readonly unread: number;
  /** The most recent message either way, or `null` for a connection nobody has written to. */
  readonly last: Message | null;
}

// ── §26.7 multiplayer ────────────────────────────────────────────────────────────────────────

/**
 * §26.7 (D-210) / §30.1 — the titles multiplayer covers, and the list is here rather than in the
 * client because the server refuses everything else.
 *
 * The first two were picked for opposite reasons and both hold: Conquest is discrete and
 * turn-bounded, so it has no latency budget to blow; Intercept is co-op, which forgives latency in
 * a way versus play does not. **Chess joins at §30.1** on Conquest's own argument — a game where a
 * move may take a minute has no latency budget either, and D-370's move-whenever falls out of the
 * session simply persisting. **Backgammon joins at M36** for the same reason, with its dice drawn from the session's shared seed so neither client ever rolls privately. Sortie follows in M41.
 */
export const MULTIPLAYER_GAME_IDS = [
  'conquest',
  'intercept',
  'chess',
  'backgammon',
  // §30.1 — Sortie's co-op (§29.11, §30.3). Everything in §26.7 carries unchanged; only the
  // exchange cadence differs, and that is a client-side declaration the server never sees.
  'sortie',
] as const;
export type MultiplayerGameId = (typeof MULTIPLAYER_GAME_IDS)[number];
export const isMultiplayerGameId = (value: string): value is MultiplayerGameId =>
  (MULTIPLAYER_GAME_IDS as readonly string[]).includes(value);

/** §17 `play_sessions.state`. */
export const PLAY_STATES = ['invited', 'active', 'finished', 'declined', 'cancelled'] as const;
export type PlayState = (typeof PLAY_STATES)[number];

/** `p_` + 128 bits of hex. Guessing one is not how anybody reaches a session, but nor is counting. */
export const PLAY_SESSION_ID_PATTERN = /^p_[0-9a-f]{32}$/;
export const isPlaySessionId = (value: string): boolean => PLAY_SESSION_ID_PATTERN.test(value);

/**
 * §26.4 — an unanswered invitation expires after ten minutes.
 *
 * The card also dies when the sender's window closes, which is the clause that matters more (D-214);
 * this is the backstop for the sender whose tab was killed rather than closed.
 */
export const PLAY_INVITE_TTL_MS = 10 * 60 * 1000;

/**
 * §26.7 — what one row of the move log carries, as three shapes.
 *
 * **The server stores the log and never the state**, so this is opaque to it with exactly two
 * exceptions, both named here rather than discovered later: `kind`, so a `sync` move may be posted
 * by whichever client noticed rather than only by the turn holder; and `next`, which is how a
 * turn-based game says the turn passed (D-349). Everything else is the game's business.
 */
export type PlayPayload =
  /** One action of a turn-based game (Conquest). `next` names who moves after it. */
  | { readonly kind: 'game'; readonly data: unknown; readonly next?: string | null }
  /**
   * One co-op input, stamped with the tick it was produced at (Intercept).
   *
   * Both clients apply it at `tick + COOP_DELAY_TICKS`, which is what makes the two logs interleave
   * into one ordered stream rather than into two guesses.
   */
  | { readonly kind: 'input'; readonly tick: number; readonly data: unknown }
  /**
   * §26.7's desync probe: a state hash at a tick, posted every `SYNC_EVERY_TICKS`.
   *
   * `seq` is the **log prefix the hash was taken at** — the seq of the last game or input move
   * folded into that state (D-442). Two clients may legitimately hold different states for the same
   * tick while one of them is a move behind, and comparing those two hashes is how a healthy game
   * used to be declared broken. With the prefix published alongside, a probe is only ever compared
   * against a hash derived from the same moves. Optional so a client mid-upgrade is ignored rather
   * than accused.
   */
  | { readonly kind: 'sync'; readonly tick: number; readonly hash: string; readonly seq?: number }
  /**
   * §26.8 (D-443) — *"I am leaving this game."*
   *
   * Presence answers "have they checked in in the last 300 seconds", which is the right question
   * for somebody who wandered off and the wrong one for somebody who closed the window: their peer
   * sat looking at a board waiting for a move that was never coming. This is the explicit signal,
   * posted on the way out, so the other end is told at once.
   *
   * **It is not terminal** (D-585). `beforeunload` cannot tell a reload from a close, so a player
   * who refreshes the page posts one of these on the way out and comes straight back — see
   * `rejoin`, which is the sentence that takes it back.
   */
  | { readonly kind: 'leave' }
  /**
   * §26.8 (D-585) — *"I am back."* The other half of `leave`, and the reason `leave` is survivable.
   *
   * D-443 gave a departure an explicit signal and made it permanent, because at the time the only
   * way to post one was to close the window for good. A reload posts one too — `beforeunload` fires
   * for both and cannot distinguish them — so refreshing a browser handed the peer a terminal
   * *"they have left"* scrim over a game the server still holds as `active`. This is what a
   * returning client posts once it has rebuilt from the log, and what clears that scrim.
   *
   * Out-of-band like `sync` and `leave`: it is a statement about a *player*, not a move in the
   * game, so it neither needs nor passes the turn.
   */
  | { readonly kind: 'rejoin' };

export interface PlayMove {
  /** Strictly increasing from 1, with no gaps. The server's only authority (§26.7). */
  readonly seq: number;
  readonly author: string;
  readonly payload: PlayPayload;
  readonly at: number;
}

/** §16 `GET /api/play/sessions/:id`. */
export interface PlaySession {
  readonly id: string;
  readonly gameId: MultiplayerGameId;
  readonly host: string;
  readonly guest: string;
  readonly hostName: string;
  readonly guestName: string;
  /** §24.4's shared seed: both clients build the same engine from it. */
  readonly seed: string;
  readonly state: PlayState;
  /** Whose turn it is, or `null` for co-op — which is how a client tells the two modes apart. */
  readonly turn: string | null;
  readonly createdAt: number;
  /** When the session became `active`; the origin of the co-op tick clock. `null` until accepted. */
  readonly activeAt: number | null;
  readonly endedAt: number | null;
  /** §30.5 — the claimed verdict, once somebody has posted one. */
  readonly result: PlayResult | null;
  /** The log from `since` onwards, oldest first. */
  readonly moves: readonly PlayMove[];
  /**
   * The server's clock at the moment it answered.
   *
   * Co-op is a shared timeline and two browsers do not agree on what time it is. Both derive their
   * tick from `activeAt` against *this* clock, so the offset each applies is measured rather than
   * assumed — a client whose system clock is a minute out plays in step anyway.
   */
  readonly now: number;
}

/**
 * §26.3 / §26.8 (D-586) — one row of Connections' *Games in progress* list.
 *
 * Deliberately **not** a `PlaySession`. The roster app needs to draw a line saying who you are
 * playing, at what, and whether it is your move; a `PlaySession` would additionally hand it the
 * seed and the entire move log of a game it is not in. So the shell narrows it on the way out and
 * resolves "which of these two refs is the other person" once, where `me` is already known.
 */
export interface LiveGame {
  readonly sessionId: string;
  readonly gameId: MultiplayerGameId;
  /** The other player. An accepted connection by construction — this app already has the roster. */
  readonly peer: string;
  readonly peerName: string;
  readonly state: 'invited' | 'active';
  /**
   * Whose move it is, from the caller's point of view, or `null` for a session that has no turn —
   * a co-op game, or an invitation nobody has answered yet.
   */
  readonly myTurn: boolean | null;
  /** When it became a game: `activeAt` if it is one, `createdAt` while it is still an offer. */
  readonly startedAt: number;
}

// ── §30.4 daily deals ────────────────────────────────────────────────────────────────────────

/**
 * `YYYY-MM-DD`, UTC. The one date format the daily deal touches.
 *
 * Here rather than in the card kit because **three places need the same answer**: the game derives
 * its seed from it, the client posts it, and the server refuses a row whose `day` is not its own
 * current UTC date. A second copy of this function is exactly the class of bug D-347 and D-355 both
 * were — a fact written down twice, where the copy nobody runs quietly goes wrong.
 */
export function utcDayKey(at: number | Date): string {
  const date = typeof at === 'number' ? new Date(at) : at;
  const pad = (value: number): string => String(value).padStart(2, '0');
  return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
}

export const DAY_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
export const isDayKey = (value: string): boolean => DAY_KEY_PATTERN.test(value);

/**
 * §30.4 — the three daily-deal titles and how each one's board is ranked.
 *
 * `win` first everywhere, because a finished deal beats an unfinished one however fast it was;
 * then the game's own tiebreak. The server upserts by this rule, and the Standings view sorts by
 * it, from this one table.
 */
export const DAILY_GAME_IDS = ['freecell', 'pyramid'] as const;
export type DailyGameId = (typeof DAILY_GAME_IDS)[number];
export const isDailyGameId = (value: string): value is DailyGameId =>
  (DAILY_GAME_IDS as readonly string[]).includes(value);

/** How a daily row is ranked: `'ms'` = win, then faster; `'score'` = higher score, then faster. */
export const DAILY_RANKING: Readonly<Record<DailyGameId, 'ms' | 'score'>> = {
  freecell: 'ms',
  pyramid: 'score',
};

/** One row of a daily board. `score` doubles as the win flag: a win scores the full 52. */
export interface DailyScore {
  readonly userRef: string;
  readonly displayName: string;
  readonly gameId: string;
  readonly day: string;
  readonly score: number;
  readonly ms: number;
  readonly moves: number;
  readonly won: boolean;
  readonly createdAt: number;
}

/** What a client posts. The caller's ref comes from the session, never from the body. */
export interface DailyScoreEntry {
  readonly gameId: string;
  readonly day: string;
  readonly score: number;
  readonly ms: number;
  readonly moves: number;
  readonly won: boolean;
}

/**
 * Is `next` a better daily row than `current`? The one comparison, shared by the server's upsert
 * and the client's board.
 *
 * A win always beats a loss. Between two of a kind the game's ranking decides, and `ms` breaks
 * every remaining tie — including `score`'s, which is why the parameter is a mode rather than a
 * comparator.
 */
export function beatsDaily(
  next: { score: number; ms: number; won: boolean },
  current: { score: number; ms: number; won: boolean },
  ranking: 'ms' | 'score',
): boolean {
  if (next.won !== current.won) return next.won;
  if (ranking === 'score' && next.score !== current.score) return next.score > current.score;
  if (next.ms !== current.ms) return next.ms < current.ms;
  return false;
}

/** §30.4 — rows older than this are dropped by the hourly sweep. */
export const DAILY_KEEP_DAYS = 35;

// ── §30.5 match results and head-to-head ─────────────────────────────────────────────────────

/** How a finished game ended. `single`/`gammon`/`backgammon` are backgammon's point values. */
export const PLAY_RESULT_KINDS = [
  'checkmate',
  'resign',
  'draw',
  'single',
  'gammon',
  'backgammon',
  'abandon',
] as const;
export type PlayResultKind = (typeof PLAY_RESULT_KINDS)[number];
export const isPlayResultKind = (value: string): value is PlayResultKind =>
  (PLAY_RESULT_KINDS as readonly string[]).includes(value);

/**
 * §30.5 (D-371) — what the client whose move ended the game claims happened.
 *
 * **Client-reported, and that is proportionate.** D-211 killed rankings, so nothing rides on a
 * result but bragging — and the move log sits beside it, so anybody who cares can replay the game
 * and check. D-234 is untouched: the server stores a verdict, it does not compute one.
 */
export interface PlayResult {
  /** The winner's ref, or `null` for a draw. */
  readonly winner: string | null;
  readonly how: PlayResultKind;
  /** Backgammon's points for the game. Absent elsewhere. */
  readonly points?: number;
}

/** One opponent's lifetime tally in one game (§30.5). Computed on read, never stored. */
export interface HeadToHead {
  readonly gameId: string;
  readonly won: number;
  readonly lost: number;
  readonly drawn: number;
}

/** §26.7 — "every 60 ticks each client posts a state hash as a move of type `sync`". */
export const SYNC_EVERY_TICKS = 60;

/** §26.7 — co-op applies both players' inputs at a **3-tick delay**. */
export const COOP_DELAY_TICKS = 3;
